// ═══════════════════════════════════════════════
// 页面级英文翻译层 (只在英文界面启用)
//
// 业务页面里有 6000 多处硬编码中文, 逐个改成 t() 要动 180 多个文件, 而且这些页面
// 还在被多人并行修改。所以采用"显示层翻译": 盯住 DOM, 把文本节点和
// placeholder/title/aria-label/alt 属性按字典换成英文; 切回中文时原样还原。
//
// 与 React 的关系: 只改 nodeValue / 属性值, 不增删节点, 所以不会打乱 React 的协调。
// React 之后再改同一个节点时, 新值与我们写入的值不同 —— 视为新的中文原文, 重新翻译。
// 相邻文本节点 (JSX 里 `第 {n} 段` 会渲染成三个节点) 合在一起按模板翻, 结果写进
// 第一个节点, 其余置空。
//
// 不翻译: 输入框、文本域、代码块、可编辑区域, 以及标了 data-i18n-skip 的区域
// (用户输入、大模型回复这类内容本身不是界面文字)。
// ═══════════════════════════════════════════════
import { hasHan, translatePreservingSpace, type TextTranslator } from './textTranslator';

// 整块不碰 (连属性也不翻) 的区域
const SKIP_SELECTOR = '[contenteditable="true"],[data-i18n-skip]';
// 内容不翻但属性照翻 (输入框的 placeholder 要翻, 用户填的值不能动)
// 原生 <select> 的 <option> 文字要翻; 没写 value 的 option 以文字作值, 翻译前先把原文钉成 value
const CONTENT_SKIP = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'CODE', 'PRE', 'NOSCRIPT']);
const ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt'] as const;

type TextState = { original: string; applied: string };

const hanCount = (text: string) => (text.match(/[\u4e00-\u9fff]/g) ?? []).length;

export class DomTranslator {
  private observer: MutationObserver | null = null;
  private texts = new WeakMap<Text, TextState>();
  private attrs = new WeakMap<Element, Map<string, TextState>>();
  private touched = new Set<Node>();
  private pending = new Set<Node>();
  private scheduled = false;

  constructor(private translator: TextTranslator, private root: HTMLElement = document.body) {}

  start() {
    if (this.observer) return;
    this.processSubtree(this.root);
    this.observer = new MutationObserver((records) => this.onMutations(records));
    this.observer.observe(this.root, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: [...ATTRIBUTES],
    });
  }

  /** 停止并把所有改过的文字还原成中文。 */
  stop() {
    this.observer?.disconnect();
    this.observer = null;
    this.pending.clear();
    for (const node of this.touched) {
      if (node instanceof Text) {
        const state = this.texts.get(node);
        if (state && node.nodeValue === state.applied) node.nodeValue = state.original;
      } else if (node instanceof Element) {
        this.attrs.get(node)?.forEach((state, name) => {
          if (node.getAttribute(name) === state.applied) node.setAttribute(name, state.original);
        });
      }
    }
    this.touched.clear();
    this.texts = new WeakMap();
    this.attrs = new WeakMap();
  }

  private skipped(el: Element | null) {
    return !el || Boolean(el.closest(SKIP_SELECTOR));
  }

  private onMutations(records: MutationRecord[]) {
    for (const r of records) {
      if (r.type === 'characterData') {
        if (r.target.parentElement) this.pending.add(r.target.parentElement);
      } else if (r.type === 'attributes') {
        this.pending.add(r.target);
      } else {
        if (r.target instanceof Element) this.pending.add(r.target);
        r.addedNodes.forEach((n) => this.pending.add(n));
      }
    }
    if (!this.scheduled) {
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        const nodes = [...this.pending];
        this.pending.clear();
        for (const node of nodes) {
          if (!node.isConnected) continue;
          if (node instanceof Element) this.processSubtree(node);
          else if (node instanceof Text && node.parentElement) this.processChildren(node.parentElement);
        }
        // 自己写入也会产生记录, 下一轮处理时译文与原文对得上, 不会再写, 循环自然停止。
        // 不能用 takeRecords() 丢弃: 同一轮里可能夹着 React 的真实修改。
      });
    }
  }

  private processSubtree(el: Element) {
    if (this.skipped(el)) return;
    this.processAttributes(el);
    if (CONTENT_SKIP.has(el.tagName)) return;
    this.processChildren(el);
    for (const child of Array.from(el.children)) this.processSubtree(child);
    this.pruneTouched();
  }

  private processAttributes(el: Element) {
    for (const name of ATTRIBUTES) {
      const current = el.getAttribute(name);
      if (current == null) continue;
      let states = this.attrs.get(el);
      const prev = states?.get(name);
      const original = prev && current === prev.applied ? prev.original : current;
      if (!hasHan(original)) continue;
      const translated = translatePreservingSpace(this.translator, original);
      if (translated == null) continue;
      if (!states) { states = new Map(); this.attrs.set(el, states); }
      states.set(name, { original, applied: translated });
      if (current !== translated) el.setAttribute(name, translated);
      this.touched.add(el);
    }
  }

  /** 把直接子节点里连续的文本节点分组, 每组整体翻译。 */
  private processChildren(el: Element) {
    if (this.skipped(el) || CONTENT_SKIP.has(el.tagName)) return;
    let run: Text[] = [];
    const flush = () => { if (run.length) this.processRun(run); run = []; };
    el.childNodes.forEach((child) => {
      if (child instanceof Text) run.push(child);
      else flush();
    });
    flush();
  }

  private originalOf(node: Text) {
    const state = this.texts.get(node);
    const value = node.nodeValue ?? '';
    return state && value === state.applied ? state.original : value;
  }

  private write(node: Text, original: string, applied: string) {
    this.texts.set(node, { original, applied });
    if (node.nodeValue !== applied) node.nodeValue = applied;
    this.touched.add(node);
  }

  private processRun(run: Text[]) {
    const originals = run.map((node) => this.originalOf(node));
    const parent = run[0]?.parentElement;
    if (parent instanceof HTMLOptionElement && !parent.hasAttribute('value')) {
      parent.setAttribute('value', originals.join(''));
    }
    const combined = originals.join('');
    if (!hasHan(combined)) {
      // React 把中文换成了非中文 (比如数字), 记录一下新原文即可
      run.forEach((node, i) => { if (this.texts.has(node)) this.write(node, originals[i], originals[i]); });
      return;
    }
    const perNode = originals.map((original) =>
      (hasHan(original) ? translatePreservingSpace(this.translator, original) ?? original : original));
    if (run.length > 1) {
      // 整串按模板翻通常更自然; 但整串只翻了一半 (分段兜底) 而逐个节点能翻得更干净时, 用逐个节点的
      const whole = translatePreservingSpace(this.translator, combined);
      if (whole != null && hanCount(whole) <= hanCount(perNode.join(''))) {
        run.forEach((node, i) => this.write(node, originals[i], i === 0 ? whole : ''));
        return;
      }
    }
    run.forEach((node, i) => {
      if (!hasHan(originals[i]) && !this.texts.has(node)) return;
      this.write(node, originals[i], perNode[i]);
    });
  }

  private pruneTouched() {
    if (this.touched.size < 5000) return;
    for (const node of this.touched) if (!node.isConnected) this.touched.delete(node);
  }
}
